import { BackendError, DEFAULT_LIMITS, type DepositDetail, type DepositFileRow, type PublicationStats } from "@opulancia/deposit-core";
import type { App } from "../app.js";
import { memberName } from "../app.js";
import { announce, formatBytes, formatDate, h, notice, plural, replace } from "../dom.js";
import { analysisSection } from "./analysis.js";
import { orderEditor, type OrderEditor } from "./reorder.js";
import { ANALYSIS, EVENTS, instagramChip, integrityChip, uploadChip } from "./status.js";

const nameOf = (f: DepositFileRow) => f.source_path.split("/").pop() ?? f.source_path;

/** A deposit: its slides in order (correctable), its statuses, the optional Instagram link, its files and history. */
export function detailView(app: App, id: string): HTMLElement {
  const root = h("div", null, h("p", { class: "muted" }, "Chargement…"));
  const backend = app.connection.backend;
  let message: HTMLElement | null = null;
  let stats: PublicationStats | null = null;

  const load = async () => {
    try {
      const detail = await backend.getDeposit(id);
      // statistics are optional: a failure to read them never blocks the deposit page
      stats = backend.getPublicationStats && detail.deposit.instagram_url ? await backend.getPublicationStats(id).catch(() => null) : null;
      const urls = new Map(await Promise.all(detail.files.filter((f) => f.role === "slide" || f.role === "extra_image").map(async (f) => [f.id, await backend.objectUrl(f.storage_path).catch(() => "")] as const)));
      paint(detail, urls);
    } catch (error) {
      replace(root, notice("bad", error instanceof BackendError && error.code === "not_found" ? "Dépôt introuvable" : "Dépôt indisponible", error instanceof Error ? error.message : String(error), h("a", { class: "btn", href: "#/bibliotheque" }, "Bibliothèque")));
    }
  };

  const fail = (error: unknown, reload = false) => {
    const conflict = error instanceof BackendError && error.code === "version_conflict";
    message = notice(conflict ? "warn" : "bad", conflict ? "Modifié entre-temps" : "Non enregistré", error instanceof Error ? error.message : String(error));
    announce(error instanceof Error ? error.message : String(error));
    if (reload || conflict) void load();
  };

  function paint(detail: DepositDetail, urls: Map<string, string>, editing = false): void {
    const d = detail.deposit;
    const slides = detail.files.filter((f) => f.role === "slide");
    const extras = detail.files.filter((f) => f.role === "extra_image");
    const complete = d.upload_status === "complete";
    let editor: OrderEditor | null = null;
    const item = (f: DepositFileRow) => ({ key: f.id, name: nameOf(f), url: urls.get(f.id) ?? "" });
    if (editing) editor = orderEditor({ slides: slides.map(item), extras: extras.map(item), maxSlides: DEFAULT_LIMITS.maxSlides });

    const order = editing
      ? h(
          "div",
          null,
          editor!.element,
          h(
            "div",
            { class: "row sticky-save", style: "margin-top:20px" },
            h(
              "button",
              {
                class: "btn primary big",
                type: "button",
                "data-testid": "save-order",
                onclick: async () => {
                  try {
                    await backend.setSlideOrder(d.id, d.version, editor!.slides(), editor!.extras());
                    message = notice("ok", "Ordre enregistré", "Les fichiers sont inchangés ; seules les positions ont changé.");
                    announce("Ordre enregistré.");
                    await load();
                  } catch (error) {
                    fail(error);
                  }
                },
              },
              "Enregistrer l'ordre",
            ),
            h("button", { class: "btn", type: "button", onclick: () => paint(detail, urls) }, "Annuler"),
          ),
        )
      : h(
          "div",
          null,
          h(
            "ol",
            { class: "slides", "aria-label": "Slides dans l'ordre" },
            slides.map((f, i) =>
              h("li", { class: "slide" }, h("div", { class: "cover" }, urls.get(f.id) ? h("img", { src: urls.get(f.id)!, alt: `Slide ${i + 1} : ${nameOf(f)}`, loading: "lazy" }) : null), h("span", { class: "pos" }, String(i + 1)), h("div", { class: "meta" }, h("div", { class: "name" }, nameOf(f)))),
            ),
          ),
          extras.length ? h("section", { class: "extras" }, h("h2", null, `Hors carrousel (${extras.length})`), h("ul", { class: "slides compact" }, extras.map((f) => h("li", { class: "slide" }, h("div", { class: "cover" }, h("img", { src: urls.get(f.id) ?? "", alt: nameOf(f), loading: "lazy" })), h("div", { class: "meta" }, h("div", { class: "name" }, nameOf(f))))))) : null,
        );

    replace(
      root,
      h(
        "div",
        { class: "detail-head" },
        h("div", null, h("a", { href: "#/bibliotheque", class: "muted", style: "font-size:14px" }, "← Bibliothèque"), h("h1", null, `Dépôt n°${d.number}`), h("div", { class: "chips row" }, uploadChip(d), integrityChip(d), instagramChip(d))),
        complete && !editing ? h("button", { class: "btn", type: "button", "data-testid": "edit-order", onclick: () => paint(detail, urls, true) }, "Modifier l'ordre") : null,
      ),
      message,
      complete ? null : h("div", { style: "margin-bottom:20px" }, notice("warn", "Envoi inachevé", "Tous les fichiers ne sont pas encore arrivés. Depuis « Ajouter », redéposez les mêmes fichiers d'origine : l'envoi reprendra où il s'est arrêté.")),
      h(
        "div",
        { class: "layout" },
        h("div", null, h("section", { "aria-label": "Slides" }, order), complete && !editing ? h("div", { style: "margin-top:24px" }, analysisSection(backend, d.id, d.number)) : null),
        h(
          "aside",
          { class: "side" },
          h(
            "div",
            { class: "panel" },
            h("h2", null, "Statut"),
            h(
              "dl",
              { class: "status-list" },
              h("div", null, h("dt", null, "Déposé"), h("dd", null, `${formatDate(d.created_at)} · ${memberName(app, d.created_by)}`)),
              h("div", null, h("dt", null, "Envoi"), h("dd", null, complete ? `complet (${formatDate(d.completed_at)})` : "en cours")),
              h("div", null, h("dt", null, "Intégrité"), h("dd", null, !complete ? "—" : d.integrity_status === "verified" ? `vérifiée (${formatDate(d.integrity_checked_at)})` : d.integrity_status === "failed" ? "en échec" : "à vérifier par le serveur")),
              h("div", null, h("dt", null, "Analyse éditoriale"), h("dd", null, ANALYSIS[d.analysis_status])),
              h("div", null, h("dt", null, "Carrousel"), h("dd", null, plural(d.slide_count, "slide", "slides"))),
            ),
            d.note ? h("p", { style: "margin:14px 0 0;font-size:14px" }, h("b", null, "Note : "), d.note) : null,
          ),
          instagramPanel(),
          statsPanel(),
          filesPanel(detail),
          h("div", { class: "panel" }, h("h2", null, "Historique"), h("ul", { class: "history" }, detail.events.map((e) => h("li", null, h("time", { datetime: e.at }, `${formatDate(e.at)} · ${memberName(app, e.actor)}`), EVENTS[e.kind] ?? e.kind)))),
        ),
      ),
    );

    function instagramPanel(): HTMLElement {
      if (!complete) return h("div", { class: "panel" }, h("h2", null, "Instagram"), h("p", { class: "muted", style: "margin:0;font-size:14px" }, "Le lien s'ajoute une fois le dépôt enregistré."));
      const urlInput = h("input", { type: "url", inputmode: "url", placeholder: "https://www.instagram.com/p/…", value: d.instagram_url ?? "", "data-testid": "instagram-url", autocomplete: "off" });
      const local = d.published_at ? new Date(new Date(d.published_at).getTime() - new Date(d.published_at).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "";
      const dateInput = h("input", { type: "datetime-local", value: local });
      return h(
        "form",
        {
          class: "panel",
          onsubmit: async (e: Event) => {
            e.preventDefault();
            try {
              const published = dateInput.value ? new Date(dateInput.value).toISOString() : null;
              await backend.linkInstagram(d.id, d.version, urlInput.value.trim() || null, published);
              message = notice("ok", urlInput.value.trim() ? "Lien Instagram enregistré" : "Lien Instagram retiré", "Les statistiques s'affichent dès qu'un relevé de cette publication est importé.");
              await load();
            } catch (error) {
              fail(error);
              paint(detail, urls);
            }
          },
        },
        h("h2", null, "Instagram (facultatif)"),
        h("div", { style: "display:grid;gap:12px" }, h("label", { class: "field" }, "Lien de la publication", urlInput), h("label", { class: "field" }, "Date de publication (facultatif)", dateInput), h("div", { class: "row" }, h("button", { class: "btn dark", type: "submit" }, d.instagram_url ? "Mettre à jour" : "Enregistrer le lien"), d.instagram_url ? h("a", { href: d.instagram_url, target: "_blank", rel: "noopener noreferrer" }, "Ouvrir") : null)),
      );
    }

    function statsPanel(): HTMLElement | null {
      if (!d.instagram_url || !stats) return null;
      if (!stats.matched || !stats.captured_at) {
        return h("div", { class: "panel" }, h("h2", null, "Statistiques"), h("p", { class: "muted", style: "margin:0;font-size:14px" }, "Aucun relevé importé pour ce lien."));
      }
      const n = (v: number | null) => (v === null ? "n/d" : new Intl.NumberFormat("fr-FR").format(v));
      const rows: Array<[string, number | null]> = [
        ["Vues", stats.views],
        ["Couverture", stats.reach],
        ["J'aime", stats.likes],
        ["Commentaires", stats.comments],
        ["Partages", stats.shares],
        ["Enregistrements", stats.saves],
        ["Abonnés gagnés", stats.followers_gained],
        ["Visites du profil", stats.profile_visits],
      ];
      const age = stats.age_hours !== null ? `âge réel ${Math.round(stats.age_hours)} h` : "âge non calculé (heure de publication non confirmée)";
      return h(
        "div",
        { class: "panel", "data-testid": "publication-stats" },
        h("h2", null, "Statistiques"),
        h("dl", { class: "status-list" }, rows.map(([label, value]) => h("div", null, h("dt", null, label), h("dd", null, n(value))))),
        h(
          "p",
          { class: "muted", style: "margin:12px 0 0;font-size:13px" },
          `Cumul depuis la publication jusqu'au relevé du ${formatDate(stats.captured_at)} (${stats.source === "meta_business_suite" ? "export Meta" : (stats.source ?? "source inconnue")}) ; ${age}. Pas un résultat à J+7.`,
          stats.published_local && !stats.published_at ? ` Heure de publication indiquée par Meta : ${stats.published_local} (fuseau non confirmé).` : "",
        ),
      );
    }

    function filesPanel(detail: DepositDetail): HTMLElement {
      const groups: Array<[string, DepositFileRow[]]> = [
        ["ZIP d'origine", detail.files.filter((f) => f.role === "original_zip")],
        ["Fichiers descriptifs", detail.files.filter((f) => f.role === "sidecar")],
        ["Images", detail.files.filter((f) => f.role === "slide" || f.role === "extra_image")],
      ];
      return h(
        "div",
        { class: "panel" },
        h("h2", null, "Originaux"),
        groups
          .filter(([, files]) => files.length)
          .map(([label, files]) =>
            h(
              "div",
              { style: "margin-bottom:10px" },
              h("div", { class: "muted", style: "font-size:13px;margin-bottom:4px" }, label),
              h("ul", { class: "files" }, files.map((f) => h("li", null, h("button", { class: "btn quiet", style: "padding:0;min-height:32px;text-align:left;white-space:normal", type: "button", onclick: () => void download(f) }, `${nameOf(f)} · ${formatBytes(f.bytes)}`)))),
            ),
          ),
      );
    }
  }

  const download = async (f: DepositFileRow) => {
    try {
      const url = await backend.objectUrl(f.storage_path, nameOf(f));
      const a = h("a", { href: url, download: nameOf(f) });
      document.body.append(a);
      a.click();
      a.remove();
    } catch (error) {
      fail(error);
    }
  };

  void load();
  return root;
}
